/**
 * GpuTelemetryChart
 *
 * ECharts line chart for Intel iGPU engine utilization + frequency.
 * Renders four lines: Render, Video, Copy (left Y-axis, %) and
 * Freq (right Y-axis, MHz) as a ghosted step chart backdrop.
 *
 * Left Y-axis defaults to dynamic scaling (peak value + 20% padding, min 10%).
 * When `yAxisLocked` is true, left Y-axis is fixed at 0-100%.
 * Right Y-axis (MHz) is always dynamic based on hardware max or observed peak.
 *
 * Canvas renderer for optimal real-time performance.
 */

import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import ReactECharts from "echarts-for-react";
import type { EChartsOption } from "echarts";

import type { GpuSample } from "../../hooks/useIntelGpuStream";
import { getTooltipBase } from "../metrics/echartsTooltipTheme";
import {
  getAxisConfig,
  createAreaGradient,
} from "../../lib/chartTheme";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ENGINE_COLORS = {
  render: "#34d399", // green
  video: "#60a5fa", // blue
  copy: "#a78bfa", // purple
  freq: "#f59e0b", // amber
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

type GpuTelemetryChartProps = {
  history: GpuSample[];
  height?: number;
  /** When true, left Y-axis is fixed at 0-100%. Right Y-axis is always dynamic. */
  yAxisLocked?: boolean;
  /** Hardware max frequency in MHz (for right Y-axis ceiling). Falls back to dynamic. */
  freqMaxMhz?: number | null;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const GpuTelemetryChart = forwardRef<ReactECharts, GpuTelemetryChartProps>(
  ({ history, height = 84, yAxisLocked = false, freqMaxMhz = null }, ref) => {
    const chartRef = useRef<ReactECharts>(null);
    const [hovered, setHovered] = useState(false);

    // Expose chartRef to parent for zoom controls
    useImperativeHandle(ref, () => chartRef.current!, []);

    // Dynamic left Y-axis ceiling (engine utilization %)
    const dynamicYMax = useMemo(() => {
      if (history.length === 0) return 10;
      let peak = 0;
      for (const s of history) {
        const { render, video, copy } = s.engines;
        if (render > peak) peak = render;
        if (video > peak) peak = video;
        if (copy > peak) peak = copy;
      }
      const padded = peak * 1.2;
      const rounded = Math.ceil(padded / 5) * 5;
      return Math.max(rounded, 10);
    }, [history]);

    const yMax = yAxisLocked ? 100 : dynamicYMax;

    // Right Y-axis ceiling (MHz frequency)
    const mhzYMax = useMemo(() => {
      if (freqMaxMhz !== null && freqMaxMhz > 0) {
        return Math.max(500, Math.ceil(freqMaxMhz / 100) * 100);
      }
      let peak = 0;
      for (const s of history) {
        if (s.freqActMhz !== null && s.freqActMhz > peak) peak = s.freqActMhz;
      }
      if (peak <= 0) return 500;
      return Math.max(500, Math.ceil((peak * 1.1) / 100) * 100);
    }, [history, freqMaxMhz]);

    const chartOption: EChartsOption = useMemo(() => {
      const axisConfig = getAxisConfig();
      const axisRgb = "148, 163, 184"; // matches --chart-axis fallback

      return {
        backgroundColor: "transparent",
        color: [ENGINE_COLORS.render, ENGINE_COLORS.video, ENGINE_COLORS.copy, ENGINE_COLORS.freq],
        tooltip: {
          ...getTooltipBase({ borderColor: ENGINE_COLORS.render }),
          formatter: (params: unknown) => {
            if (!Array.isArray(params) || params.length === 0) return "";
            const first = params[0] as { axisValue?: string | number };
            const header =
              typeof first?.axisValue === "string" ||
              typeof first?.axisValue === "number"
                ? `<div style="font-weight:600;margin-bottom:4px">${new Date(first.axisValue).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</div>`
                : "";
            const rows = params
              .map((param) => {
                if (typeof param !== "object" || param === null) return null;
                const p = param as {
                  seriesName?: string;
                  value?: number | [number, number | null];
                  color?: string;
                };
                const name = typeof p.seriesName === "string" ? p.seriesName : "";
                const raw = Array.isArray(p.value) ? p.value[1] : p.value;
                const isFreq = name === "Freq";
                const value =
                  typeof raw === "number"
                    ? isFreq
                      ? `${Math.round(raw)} MHz`
                      : `${raw.toFixed(1)}%`
                    : "\u2014";
                const color =
                  typeof p.color === "string" ? p.color : "currentColor";
                return `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${color};margin-right:6px"></span>${name}: ${value}`;
              })
              .filter(Boolean)
              .join("<br/>");
            return header + rows;
          },
        },
        legend: { show: false },
        grid: { left: 2, right: 2, top: 2, bottom: 2, containLabel: true },
        xAxis: {
          type: "time",
          boundaryGap: ["2%", "2%"],
          ...axisConfig,
          axisLabel: { ...axisConfig.axisLabel, show: false },
        },
        yAxis: [
          {
            type: "value",
            min: 0,
            max: yMax,
            axisLine: { show: false },
            splitLine: {
              lineStyle: { color: `rgba(${axisRgb}, 0.04)` },
            },
            axisLabel: {
              color: `rgba(${axisRgb}, 0.2)`,
              fontSize: 9,
              formatter: (value: number) => `${value}%`,
            },
          },
          {
            type: "value",
            min: 0,
            max: mhzYMax,
            axisLine: { show: false },
            splitLine: { show: false },
            axisLabel: {
              color: `rgba(${axisRgb}, 0.2)`,
              fontSize: 9,
            },
          },
        ],
        series: [
          {
            name: "Render",
            type: "line",
            z: 2,
            smooth: true,
            symbol: "none",
            lineStyle: { width: 1.5 },
            emphasis: { focus: "series" },
            areaStyle: {
              color: createAreaGradient(ENGINE_COLORS.render, 0.2, 0.02),
            },
          },
          {
            name: "Video",
            type: "line",
            z: 2,
            smooth: true,
            symbol: "none",
            lineStyle: { width: 1.5 },
            emphasis: { focus: "series" },
            areaStyle: {
              color: createAreaGradient(ENGINE_COLORS.video, 0.2, 0.02),
            },
          },
          {
            name: "Copy",
            type: "line",
            z: 2,
            smooth: true,
            symbol: "none",
            lineStyle: { width: 1.5 },
            emphasis: { focus: "series" },
            areaStyle: {
              color: createAreaGradient(ENGINE_COLORS.copy, 0.15, 0.02),
            },
          },
          {
            name: "Freq",
            type: "line",
            z: 1,
            step: "end",
            symbol: "none",
            yAxisIndex: 1,
            lineStyle: { width: 0.75, opacity: 0.2 },
            areaStyle: {
              color: createAreaGradient(ENGINE_COLORS.freq, 0.05, 0.005),
            },
            emphasis: {
              focus: "series",
              lineStyle: { width: 1.5, opacity: 0.85 },
              areaStyle: {
                color: createAreaGradient(ENGINE_COLORS.freq, 0.2, 0.02),
              },
            },
            blur: {
              lineStyle: { opacity: 0.06 },
              areaStyle: { opacity: 0.01 },
            },
          },
        ],
        dataZoom: [{ type: "inside", filterMode: "none" }],
        animation: false,
      };
    }, [yMax, mhzYMax]);

    // Push data + yAxis max via setOption (skip while hovered so tooltip stays stable)
    useEffect(() => {
      if (hovered) return;
      const instance = chartRef.current?.getEchartsInstance();
      if (!instance) return;
      instance.setOption(
        {
          yAxis: [{ max: yMax }, { max: mhzYMax }],
          series: [
            { name: "Render", data: history.map((s) => [s.ts, s.engines.render]) },
            { name: "Video", data: history.map((s) => [s.ts, s.engines.video]) },
            { name: "Copy", data: history.map((s) => [s.ts, s.engines.copy]) },
            { name: "Freq", data: history.map((s) => [s.ts, s.freqActMhz]) },
          ],
        },
        { notMerge: false, lazyUpdate: true, silent: true },
      );
    }, [history, yMax, mhzYMax, hovered]);

    // Resize observer
    useEffect(() => {
      const chart = chartRef.current?.getEchartsInstance();
      if (!chart) return;
      const dom = chart.getDom();
      if (!dom) return;
      const observer = new ResizeObserver(() => chart.resize());
      observer.observe(dom);
      return () => observer.disconnect();
    }, []);

    const handleMouseLeave = () => {
      setHovered(false);
      chartRef.current?.getEchartsInstance()?.dispatchAction({ type: "hideTip" });
    };

    return (
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={handleMouseLeave}
      >
        <ReactECharts
          ref={chartRef}
          option={chartOption}
          style={{ height, width: "100%" }}
          opts={{ renderer: "canvas" }}
        />
      </div>
    );
  },
);

GpuTelemetryChart.displayName = "GpuTelemetryChart";
